import type { FormEvent, ReactNode } from 'react';
import { useState } from 'react';
import { type AdminUser, api } from '../api.ts';
import { CheckIcon, ShieldIcon, TrashIcon, UserIcon } from '../Icon.tsx';
import { ErrorNotice } from './States.tsx';

/**
 * Yönetici kullanıcı yönetimi (V6-04).
 *
 * Son admin koruması, kendi hesabını kilitleme ve oturum iptali sunucuda da
 * uygulanır; arayüz yalnızca kullanıcıyı yanlış yolculuğa sokmaz.
 */
export function AdminUsersPanel({
  users,
  currentUserId,
  onChanged,
}: {
  users: AdminUser[];
  currentUserId: string;
  onChanged: () => void;
}): ReactNode {
  const [form, setForm] = useState({
    username: '',
    displayName: '',
    role: 'user' as 'user' | 'admin',
    password: '',
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (
    key: string,
    action: () => Promise<void>,
    success: string,
  ): Promise<boolean> => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
      onChanged();
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'İşlem tamamlanamadı.');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const create = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const created = await run(
      'create',
      async () => {
        await api.adminCreateUser(form);
      },
      'Kullanıcı oluşturuldu.',
    );
    if (created) setForm({ username: '', displayName: '', role: 'user', password: '' });
  };

  const toggleRole = (user: AdminUser): void => {
    const next = user.role === 'admin' ? 'user' : 'admin';
    if (
      !window.confirm(
        `${user.displayName} ${next === 'admin' ? 'yönetici' : 'kullanıcı'} yapılsın mı?`,
      )
    ) {
      return;
    }
    void run(
      `role:${user.id}`,
      async () => {
        await api.adminUpdateUser(user.id, { role: next });
      },
      'Rol güncellendi.',
    );
  };

  const toggleDisabled = (user: AdminUser): void => {
    const disable = user.disabledAt === null;
    if (
      disable &&
      !window.confirm(`${user.displayName} devre dışı bırakılsın mı? Açık oturumları kapatılacak.`)
    ) {
      return;
    }
    void run(
      `disabled:${user.id}`,
      async () => {
        await api.adminUpdateUser(user.id, { disabled: disable });
      },
      disable ? 'Kullanıcı devre dışı bırakıldı.' : 'Kullanıcı yeniden etkinleştirildi.',
    );
  };

  const resetPassword = (user: AdminUser): void => {
    const password = window.prompt(
      `${user.displayName} için yeni geçici parola (en az 10 karakter):`,
    );
    if (password === null) return;
    void run(
      `password:${user.id}`,
      async () => {
        await api.adminResetUserPassword(user.id, password);
      },
      'Parola güncellendi; kullanıcının açık oturumları kapatıldı.',
    );
  };

  const revokeSessions = (user: AdminUser): void => {
    if (!window.confirm(`${user.displayName} kullanıcısının tüm oturumları kapatılsın mı?`)) return;
    void run(
      `sessions:${user.id}`,
      async () => {
        await api.adminRevokeUserSessions(user.id);
      },
      'Kullanıcı oturumları kapatıldı.',
    );
  };

  return (
    <section className="admin__panel" aria-label="Kullanıcı yönetimi">
      <form className="admin__user-form" onSubmit={(event) => void create(event)}>
        <div className="admin__user-form-head">
          <h2>Yeni kullanıcı</h2>
          <span className="admin__threshold">Parola en az 10 karakter ve iki karakter sınıfı</span>
        </div>
        <div className="admin__user-form-grid">
          <label className="field">
            <span className="field__label">Kullanıcı adı</span>
            <input
              className="account__input"
              value={form.username}
              onChange={(event) => setForm((value) => ({ ...value, username: event.target.value }))}
              required
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span className="field__label">Görünen ad</span>
            <input
              className="account__input"
              value={form.displayName}
              onChange={(event) =>
                setForm((value) => ({ ...value, displayName: event.target.value }))
              }
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Rol</span>
            <select
              className="account__input"
              value={form.role}
              onChange={(event) =>
                setForm((value) => ({ ...value, role: event.target.value as 'user' | 'admin' }))
              }
            >
              <option value="user">Kullanıcı</option>
              <option value="admin">Yönetici</option>
            </select>
          </label>
          <label className="field">
            <span className="field__label">Geçici parola</span>
            <input
              className="account__input"
              type="password"
              value={form.password}
              onChange={(event) => setForm((value) => ({ ...value, password: event.target.value }))}
              required
              autoComplete="new-password"
            />
          </label>
        </div>
        <button type="submit" className="button button--primary" disabled={busy !== null}>
          <UserIcon size={15} />
          Kullanıcı oluştur
        </button>
      </form>

      {error !== null && <ErrorNotice message={error} />}
      {notice !== null && (
        <p className="admin__notice" role="status">
          <CheckIcon size={15} /> {notice}
        </p>
      )}

      <ul className="admin__list">
        {users.map((entry) => {
          const self = entry.id === currentUserId;
          return (
            <li
              key={entry.id}
              className={`admin__user ${entry.disabledAt === null ? '' : 'admin__user--disabled'}`}
            >
              <div className="admin__user-main">
                <div className="admin__item-title-row">
                  <strong>{entry.displayName}</strong>
                  <span>@{entry.username}</span>
                  <span
                    className={`admin__badge ${entry.role === 'admin' ? 'admin__badge--admin' : ''}`}
                  >
                    {entry.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
                  </span>
                  {entry.disabledAt !== null && (
                    <span className="admin__badge admin__badge--disabled">Devre dışı</span>
                  )}
                  {entry.has2fa && <span className="admin__badge admin__badge--2fa">2FA</span>}
                </div>
                <p className="admin__item-meta">
                  {entry.activeSessions} açık oturum
                  {entry.createdAt > 0
                    ? ` · ${new Intl.DateTimeFormat('tr-TR', { dateStyle: 'medium' }).format(new Date(entry.createdAt))}`
                    : ''}
                </p>
              </div>
              <div className="admin__user-actions">
                {entry.role === 'admin' && <ShieldIcon size={14} aria-hidden="true" />}
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={busy !== null || self}
                  onClick={() => toggleRole(entry)}
                >
                  {entry.role === 'admin' ? 'Kullanıcı yap' : 'Yönetici yap'}
                </button>
                <button
                  type="button"
                  className={`button button--ghost ${entry.disabledAt === null ? 'admin__danger' : ''}`}
                  disabled={busy !== null || self}
                  onClick={() => toggleDisabled(entry)}
                >
                  {entry.disabledAt === null ? 'Devre dışı bırak' : 'Etkinleştir'}
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={busy !== null}
                  onClick={() => resetPassword(entry)}
                >
                  Parola sıfırla
                </button>
                <button
                  type="button"
                  className="button button--ghost admin__danger"
                  disabled={busy !== null || self || entry.activeSessions === 0}
                  onClick={() => revokeSessions(entry)}
                >
                  <TrashIcon size={14} />
                  Oturumları kapat
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
